import axios from "axios"

export async function createDocument(data) {
    return await axios.post("facturacioncl/document", data)
        .then(res => res.data)
        .catch(e => {
            if (e.response && e.response.data && e.response.data.error) {
                throw e.response.data.error
            }
            if (e.response && e.response.data) {
                throw e.response.data
            }
            throw e
        })
}

export async function fetchDocuments(query) {
    return await axios.get("facturacioncl/document?" + query)
        .then(res => res.data)
        .catch(e => {
            throw (e.response && e.response.data && e.response.data.error) || e
        })
}

export async function getDocument(id) {
    return await axios.get("facturacioncl/document/" + id)
        .then(res => res.data)
        .catch(e => {
            throw (e.response && e.response.data && e.response.data.error) || e
        })
}

export async function downloadDocumentPdf(id) {
    return await axios.get("facturacioncl/document/" + id + "/pdf", { responseType: "blob" })
        .then(res => {
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }))
            window.open(url)
            return true
        })
        .catch(async e => {
            let message = "No se pudo obtener el PDF"
            if (e.response && e.response.data) {
                try {
                    const text = await e.response.data.text()
                    const parsed = JSON.parse(text)
                    message = parsed.error || message
                } catch (err) {
                    message = "No se pudo obtener el PDF"
                }
            }
            throw message
        })
}

export function mapDocumentToInvoiceRow(doc, customerName, workorder) {
    const raw = Array.isArray(doc.detalle) && doc.detalle.length
        ? doc.detalle
        : ((workorder && workorder.products) || [])
    const details = raw.map((item, index) => ({
        nro_linea: index + 1,
        sku: item.sku || (item.library_product && item.library_product.sku) || "",
        name: item.name || "",
        qty: Number(item.qty || 0),
        price: Number(item.price || 0),
    }))
    return {
        id: doc.dte || ("fcl-" + doc.id),
        sf_id: doc.id,
        fcl_id: doc.id,
        source: "facturacioncl",
        document_id: doc.folio,
        date_emited: doc.created_at,
        payment_date: doc.payment_date || null,
        payment: doc.payment,
        rznSocRecep: doc.customer_name || customerName || "",
        rutRecep: doc.customer_rut || "",
        work_order: doc.workorder,
        sales_note: doc.sales_note,
        get_total: doc.total,
        get_subtotal: doc.neto,
        get_iva: doc.iva_amount,
        get_payed: Number(doc.payed || doc.get_payed || 0),
        iva: doc.neto ? Math.round((Number(doc.iva_amount || 0) / Number(doc.neto)) * 100) : 19,
        tipo: doc.tipo,
        tipo_display: doc.tipo_display,
        estatus: doc.estatus || 1,
        payments: doc.payments || [],
        referencia_folio: doc.referencia_folio || null,
        referencia_tipo: doc.referencia_tipo || null,
        referencia_tipo_display: doc.referencia_tipo_display || null,
        motivo: doc.motivo || null,
        motivo_display: doc.motivo_display || null,
        nc_reference: doc.nc_reference || null,
        details,
    }
}

// ---- Distribuciones de la OT y guías de despacho masivas (pre-guías por local) ----
function _bulkError(e) {
    if (e.response && e.response.data && e.response.data.error) {
        throw e.response.data.error
    }
    if (e.response && e.response.status === 403) {
        throw (e.response.data && e.response.data.detail) || "No tiene permiso para esta acción"
    }
    throw e
}

export async function listDispatchBatches(params = {}) {
    return await axios.get("facturacioncl/dispatch-batch", { params }).then(res => res.data).catch(_bulkError)
}

export async function getDispatchBatch(id) {
    return await axios.get("facturacioncl/dispatch-batch/" + id).then(res => res.data).catch(_bulkError)
}

export async function deleteDispatchBatch(id) {
    return await axios.delete("facturacioncl/dispatch-batch/" + id).catch(_bulkError)
}

// Resumen por producto de la OT: cotizado / distribuido / emitido
export async function getDispatchSummary(workorderId) {
    return await axios.get("facturacioncl/dispatch-batch/summary", { params: { workorder: workorderId } })
        .then(res => res.data).catch(_bulkError)
}

function _saveBlob(data, filename) {
    const url = window.URL.createObjectURL(new Blob([data]))
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    a.click()
    window.URL.revokeObjectURL(url)
}

async function _blobError(e) {
    if (e.response && e.response.data instanceof Blob) {
        try {
            const body = JSON.parse(await e.response.data.text())
            if (body.error) throw body.error
        } catch (parsed) {
            if (typeof parsed === "string") throw parsed
        }
    }
    _bulkError(e)
}

// Plantilla con todos los productos de la cotización; con batchId se descarga la distribución prellenada
export async function downloadDispatchTemplate({ workorderId = null, batchId = null } = {}) {
    const params = batchId ? { batch_id: batchId } : { workorder_id: workorderId }
    return await axios.get("facturacioncl/dispatch-batch/template", { params, responseType: "blob" })
        .then(res => {
            const suffix = batchId ? "dist_" + batchId : "plantilla"
            _saveBlob(res.data, "distribucion_ot_" + (workorderId || "") + "_" + suffix + ".xlsx")
        })
        .catch(_blobError)
}

// fields: { file, workorder_id | batch_id, params?, mapping?, name?, dry_run? }
export async function uploadDispatchDistribution(fields) {
    const form = new FormData()
    Object.keys(fields).forEach(k => {
        const v = fields[k]
        if (v === null || v === undefined || v === "") return
        form.append(k, k === "params" || k === "mapping" ? JSON.stringify(v) : v)
    })
    return await axios.post("facturacioncl/dispatch-batch/upload", form).then(res => res.data).catch(e => {
        if (e.response && e.response.data && e.response.data.errors && e.response.data.errors.length) {
            throw e.response.data.error + ": " + e.response.data.errors.slice(0, 3).join(" | ")
        }
        _bulkError(e)
    })
}

export async function updateDispatchParams(id, params) {
    return await axios.patch("facturacioncl/dispatch-batch/" + id + "/params", params).then(res => res.data).catch(_bulkError)
}

export async function startDispatchBatch(id, payload = {}) {
    return await axios.post("facturacioncl/dispatch-batch/" + id + "/start", payload).then(res => res.data).catch(_bulkError)
}

export async function resumeDispatchBatch(id) {
    return await axios.post("facturacioncl/dispatch-batch/" + id + "/resume", {}).then(res => res.data).catch(_bulkError)
}

export async function pauseDispatchBatch(id) {
    return await axios.post("facturacioncl/dispatch-batch/" + id + "/pause", {}).then(res => res.data).catch(_bulkError)
}

export async function stopDispatchBatch(id) {
    return await axios.post("facturacioncl/dispatch-batch/" + id + "/stop", {}).then(res => res.data).catch(_bulkError)
}

export async function exportDispatchBatch(id) {
    return await axios.get("facturacioncl/dispatch-batch/" + id + "/export", { responseType: "blob" })
        .then(res => _saveBlob(res.data, "guias_distribucion_" + id + ".xlsx"))
        .catch(_blobError)
}

export async function updateDispatchItem(id, data) {
    return await axios.patch("facturacioncl/dispatch-item/" + id, data).then(res => res.data).catch(_bulkError)
}

export async function deleteDispatchItem(id) {
    return await axios.delete("facturacioncl/dispatch-item/" + id).catch(_bulkError)
}

export async function requeueDispatchItem(id) {
    return await axios.post("facturacioncl/dispatch-item/" + id + "/requeue", {}).then(res => res.data).catch(_bulkError)
}
